import { useEffect, useState } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin } from "@/lib/api";
import { Tv, ListMusic, ImageIcon, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import BusinessesOverview from "@/components/BusinessesOverview";

export default function Overview() {
  const { activeBusiness } = useBusiness();
  const [stats, setStats] = useState({ tvs: 0, online: 0, playlists: 0, media: 0 });
  const [tvs, setTvs] = useState([]);

  useEffect(() => {
    const run = async () => {
      if (!activeBusiness) return;
      const [tvRes, plRes, mdRes] = await Promise.all([
        apiAdmin.get(`/tvs?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
        apiAdmin.get(`/media?business_id=${activeBusiness.id}`),
      ]);
      const online = tvRes.data.filter((t) => t.last_seen && Date.now() - new Date(t.last_seen).getTime() < 120000).length;
      setTvs(tvRes.data);
      setStats({
        tvs: tvRes.data.length,
        online,
        playlists: plRes.data.length,
        media: mdRes.data.length,
      });
    };
    run();
  }, [activeBusiness]);

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Dashboard</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            {activeBusiness?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{activeBusiness?.description || "Welcome back."}</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KPI label="Active TVs" value={`${stats.online}/${stats.tvs}`} icon={Tv} accent="emerald" testid="kpi-tvs" />
        <KPI label="Playlists" value={stats.playlists} icon={ListMusic} accent="blue" testid="kpi-playlists" />
        <KPI label="Media files" value={stats.media} icon={ImageIcon} accent="violet" testid="kpi-media" />
        <KPI label="System health" value="99.9%" icon={Activity} accent="amber" testid="kpi-health" />
      </div>

      <BusinessesOverview />

      {/* TVs preview */}
      <section className="rounded-2xl border border-slate-200 bg-white" data-testid="overview-tvs-section">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Connected screens</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">Televisions in {activeBusiness?.name}</h2>
          </div>
          <Link
            to="/app/tvs"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            data-testid="overview-go-tvs"
          >
            Manage TVs →
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {tvs.length === 0 && (
            <div className="px-6 py-10 text-sm text-slate-500 text-center">No TVs yet for this business.</div>
          )}
          {tvs.map((tv) => (
            <div key={tv.id} className="px-6 py-4 flex items-center justify-between" data-testid={`overview-tv-row-${tv.id}`}>
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-slate-100 grid place-items-center text-slate-600">
                  <Tv className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-medium text-slate-900">{tv.name}</div>
                  <div className="text-xs text-slate-500">{tv.location} · @{tv.username}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={tv.status} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent, testid }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`h-8 w-8 rounded-lg grid place-items-center ${colorMap[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-heading text-3xl font-semibold text-slate-900 mt-3">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const isOnline = status === "online";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
        isOnline ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isOnline ? "bg-emerald-500" : "bg-slate-400"}`} />
      {isOnline ? "Online" : "Offline"}
    </span>
  );
}
